import { createFileRoute, Link } from "@tanstack/react-router";
import { Cookie, Settings2 } from "lucide-react";
import { openCookiePreferences } from "@/lib/cookie-consent";

export const Route = createFileRoute("/politica-de-cookies")({
  head: () => ({
    meta: [
      { title: "Política de Cookies — Kentro Sistemas" },
      {
        name: "description",
        content:
          "Política de Cookies da Kentro: o que são cookies, como utilizamos, categorias, cookies de terceiros (Google, Meta, TikTok) e como gerenciar seu consentimento.",
      },
      { property: "og:title", content: "Política de Cookies — Kentro Sistemas" },
      {
        property: "og:description",
        content:
          "Como a Kentro utiliza cookies e tecnologias semelhantes, conforme a LGPD, e como você pode gerenciar suas preferências.",
      },
      { property: "og:url", content: "https://kentro.com.br/politica-de-cookies" },
    ],
    links: [{ rel: "canonical", href: "https://kentro.com.br/politica-de-cookies" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Política de Cookies — Kentro Sistemas",
          url: "https://kentro.com.br/politica-de-cookies",
          inLanguage: "pt-BR",
          dateModified: "2026-06-24",
          publisher: {
            "@type": "Organization",
            name: "Barbalho Solutions Ltda.",
          },
        }),
      },
    ],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <Cookie className="size-3.5" /> LGPD · Lei 13.709/2018
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            Política de <span className="gradient-text">Cookies</span>
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Última atualização: 24 de junho de 2026.
          </p>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground">
            Esta Política explica o que são cookies, como a{" "}
            <strong className="text-foreground">Barbalho Solutions Ltda.</strong>{" "}
            (CNPJ nº 43.406.507/0001-52) utiliza essas tecnologias em seu site e quais são as
            suas opções de controle. Ela complementa a nossa{" "}
            <Link to="/privacidade" className="text-primary-glow hover:underline">
              Política de Privacidade
            </Link>{" "}
            e deve ser lida em conjunto com ela.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={openCookiePreferences}
              className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-foreground hover:bg-white/10"
            >
              <Settings2 className="size-4" /> Preferências de cookies
            </button>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="prose-kentro space-y-10 text-foreground [&_a]:text-primary-glow [&_strong]:text-foreground">
          <Block title="1. O que são cookies">
            <p>
              Cookies são pequenos arquivos de texto armazenados no seu navegador ou dispositivo
              quando você visita um site. Eles permitem que o site funcione corretamente, lembre
              suas preferências, entenda como você o utiliza e personalize conteúdos e anúncios.
            </p>
            <p>
              Além dos cookies, utilizamos tecnologias semelhantes, como <strong>pixels de
              rastreamento</strong> (tags), <strong>web beacons</strong> e identificadores de
              dispositivo, que cumprem funções parecidas. Nesta Política, o termo "cookies"
              abrange todas essas tecnologias.
            </p>
          </Block>

          <Block title="2. Por que utilizamos cookies">
            <ul>
              <li>Garantir o funcionamento técnico e a segurança do site;</li>
              <li>Lembrar suas preferências e melhorar sua experiência de navegação;</li>
              <li>Medir e analisar o desempenho e o uso do site;</li>
              <li>
                Veicular e mensurar campanhas de marketing e publicidade em plataformas de
                terceiros (Google, Meta e TikTok).
              </li>
            </ul>
          </Block>

          <Block title="3. Tipos de cookies que utilizamos">
            <h3 className="text-base font-semibold text-foreground">3.1. Estritamente necessários</h3>
            <p>
              Essenciais para o funcionamento do site e para recursos básicos de segurança. Sem
              eles, o site não funciona adequadamente. Por essa razão, <strong>não dependem de
              consentimento</strong> e não podem ser desativados pelo nosso banner.
            </p>
            <h3 className="text-base font-semibold text-foreground">3.2. Desempenho e análise</h3>
            <p>
              Coletam informações sobre como os visitantes utilizam o site (páginas acessadas,
              tempo de permanência, origem do tráfego), de forma agregada, para nos ajudar a
              melhorar o conteúdo e a navegação.
            </p>
            <h3 className="text-base font-semibold text-foreground">3.3. Funcionalidade</h3>
            <p>
              Permitem que o site lembre escolhas que você faz (como idioma ou preferências) para
              oferecer uma experiência mais personalizada.
            </p>
            <h3 className="text-base font-semibold text-foreground">3.4. Marketing e publicidade</h3>
            <p>
              Utilizados para apresentar anúncios mais relevantes, medir a eficácia das campanhas
              e evitar a repetição excessiva de anúncios. Esses cookies são definidos por nós e
              por parceiros de publicidade, e podem rastrear sua navegação em outros sites.
            </p>
          </Block>

          <Block title="4. Cookies e tecnologias de terceiros">
            <p>
              Trabalhamos com plataformas de terceiros que instalam seus próprios cookies e
              pixels quando você navega em nosso site. Cada uma trata os dados de acordo com suas
              próprias políticas, sobre as quais recomendamos a leitura.
            </p>
            <div className="-mx-2 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-foreground">
                    <th className="py-2 pr-4 font-semibold">Plataforma</th>
                    <th className="py-2 pr-4 font-semibold">Finalidade</th>
                    <th className="py-2 pr-4 font-semibold">Categoria</th>
                    <th className="py-2 font-semibold">Política</th>
                  </tr>
                </thead>
                <tbody className="align-top text-muted-foreground">
                  <tr className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium text-foreground">Google Analytics</td>
                    <td className="py-2 pr-4">Análise de tráfego e comportamento</td>
                    <td className="py-2 pr-4">Desempenho / Análise</td>
                    <td className="py-2">
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com</a>
                    </td>
                  </tr>
                  <tr className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium text-foreground">Google Ads</td>
                    <td className="py-2 pr-4">Mensuração de conversões e remarketing</td>
                    <td className="py-2 pr-4">Marketing / Publicidade</td>
                    <td className="py-2">
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com</a>
                    </td>
                  </tr>
                  <tr className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium text-foreground">Meta Pixel</td>
                    <td className="py-2 pr-4">Campanhas e públicos personalizados (Facebook/Instagram)</td>
                    <td className="py-2 pr-4">Marketing / Publicidade</td>
                    <td className="py-2">
                      <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">facebook.com/privacy</a>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-foreground">TikTok Pixel</td>
                    <td className="py-2 pr-4">Mensuração e otimização de anúncios</td>
                    <td className="py-2 pr-4">Marketing / Publicidade</td>
                    <td className="py-2">
                      <a href="https://www.tiktok.com/legal/page/row/privacy-policy/pt-BR" target="_blank" rel="noopener noreferrer">tiktok.com/legal</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Esses parceiros podem combinar os dados coletados em nosso site com outras
              informações que já possuem sobre você, conforme as respectivas políticas.
            </p>
          </Block>

          <Block title="5. Base legal para o uso de cookies">
            <p>Conforme a LGPD (Lei nº 13.709/2018):</p>
            <ul>
              <li>
                <strong>Cookies estritamente necessários:</strong> base no <strong>legítimo
                interesse</strong> (art. 7º, IX), por serem indispensáveis ao funcionamento do site.
              </li>
              <li>
                <strong>Cookies de análise, funcionalidade e marketing:</strong> dependem do seu
                <strong> consentimento</strong> (art. 7º, I), manifestado por meio do banner. Você
                pode concedê-lo, recusá-lo ou revogá-lo a qualquer momento.
              </li>
            </ul>
          </Block>

          <Block title="6. Como gerenciar seu consentimento e suas preferências">
            <ul>
              <li>
                <strong>Banner de consentimento:</strong> ao acessar o site, você pode aceitar,
                recusar ou personalizar os cookies não essenciais. É possível alterar sua escolha
                a qualquer momento clicando em{" "}
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  className="text-primary-glow underline-offset-2 hover:underline"
                >
                  Preferências de cookies
                </button>{" "}
                (também disponível no rodapé do site).
              </li>
              <li>
                <strong>Configurações do navegador:</strong> a maioria dos navegadores permite
                bloquear ou excluir cookies:
                <ul>
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/pt-BR/kb/protecao-aprimorada-contra-rastreamento-firefox-desktop" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                  <li><a href="https://support.microsoft.com/pt-br/microsoft-edge" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                  <li><a href="https://support.apple.com/pt-br/guide/safari/welcome/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                </ul>
              </li>
              <li>
                <strong>Opt-out das plataformas de publicidade:</strong>
                <ul>
                  <li>Google: <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">adssettings.google.com</a></li>
                  <li>Meta: <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer">facebook.com/settings</a></li>
                  <li>TikTok: nas configurações de anúncios da sua conta TikTok.</li>
                </ul>
              </li>
            </ul>
            <p>
              <strong>Importante:</strong> ao bloquear ou excluir cookies, algumas funcionalidades
              do site podem deixar de funcionar corretamente. Os cookies estritamente necessários
              não podem ser desativados sem comprometer o funcionamento do site.
            </p>
          </Block>

          <Block title="7. Por quanto tempo os cookies permanecem">
            <ul>
              <li>
                <strong>Cookies de sessão:</strong> temporários, excluídos automaticamente quando
                você fecha o navegador.
              </li>
              <li>
                <strong>Cookies persistentes:</strong> permanecem por um período determinado ou
                até serem excluídos manualmente. O prazo varia conforme a finalidade e a
                plataforma responsável.
              </li>
            </ul>
          </Block>

          <Block title="8. Alterações nesta Política de Cookies">
            <p>
              Podemos atualizar esta Política periodicamente para refletir mudanças nas
              tecnologias que utilizamos ou na legislação aplicável. As alterações serão
              publicadas nesta página, com atualização da data no topo.
            </p>
          </Block>

          <Block title="9. Contato">
            <ul>
              <li><strong>E-mail:</strong> <a href="mailto:contato@kentro.com.br">contato@kentro.com.br</a></li>
              <li><strong>Encarregado (DPO):</strong> <a href="mailto:dpo@kentro.com.br">dpo@kentro.com.br</a></li>
              <li><strong>Endereço:</strong> Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP, CEP 06454-000</li>
            </ul>
          </Block>
        </div>

        <p className="mt-12 text-sm text-muted-foreground">
          Documentos relacionados:{" "}
          <Link to="/privacidade" className="text-primary-glow hover:underline">
            Política de Privacidade
          </Link>
          .
        </p>
      </article>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-muted-foreground [&_li]:my-1 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 [&_ul_ul]:mt-2">
        {children}
      </div>
    </section>
  );
}
